/**
 * C-18 配送便（FR-13・T-D19）＋**配送料の設定**（FR-131・D-90・M1.6）。
 *
 * **なぜ M1 に要るか**：便が1つも無いと、職人のチェックアウト（A-06）で配送便を選べず
 * 注文が送れない。API は M0/M1 で揃っていたのに画面が無く、stg/prod で便を作る手段が
 * API 直叩きしか無かった（監査 masters#1）＝ここが埋まらないと本番で発注が成立しない。
 *
 * **なぜ配送料もここか**（05 §6.1 C-18）：置き場の第一候補だった会社設定（C-11）は M2 で
 * `GET/PATCH /console/settings` 自体が無く、そこを待つと **M1.6 で配送料を設定できる画面が
 * 1つも無くなる**。配送便と配送料は卸の頭の中で同じ「配送の設定」なので、ここに同居させる。
 *
 * 認可（03 §7）：**新設は admin のみ・更新（締め時刻や並び、利用停止）は staff も可**。
 * 画面ごと admin 限定にしない（現場のスタッフが締め時刻を直せる粒度を保つ）。
 * **配送料は他のマスタと同じ＝admin CRUD・staff R**（認可マトリクスに新しい行を足さない）。
 */

import {
  ApiError,
  auth,
  console_,
  type Bin,
  type DeliveryFeeSetting,
} from "@kenzai/api-client";
import {
  Badge,
  Card,
  ConfirmDialog,
  Empty,
  ErrorNote,
  PageHeader,
  Spinner,
  Table,
  Td,
} from "@kenzai/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { adminOnlyNote, hintStyle, inputStyle, labelStyle, overlayStyle, warnBanner } from "./formStyles";
import { deliveryFeePreview } from "./orderDisplay";

export function Bins() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["bins", "all"], queryFn: () => console_.bins(true) });
  const me = useQuery({ queryKey: ["me"], queryFn: () => auth.me(), staleTime: Infinity });
  const isAdmin = me.data?.role === "admin";

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Bin | null>(null);
  const [toggling, setToggling] = useState<Bin | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["bins"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const create = useMutation({
    mutationFn: (body: { name: string; cutoff_time: string; sort_order: number }) =>
      console_.createBin(body),
    onSuccess: () => {
      setCreating(false);
      invalidate();
    },
  });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      console_.patchBin(id, body),
    onSettled: () => {
      setEditing(null);
      setToggling(null);
      invalidate();
    },
  });

  const rows = list.data ?? [];
  const activeCount = rows.filter((b) => b.status === "active").length;

  return (
    <>
      <PageHeader
        title="配送便"
        subtitle={`納品する便と締め時刻。取引先はここで登録した便から選んで注文します。${isAdmin ? "" : adminOnlyNote("追加・変更")}`}
        actions={
          isAdmin ? (
            <button type="button" data-testid="bin-new" onClick={() => setCreating(true)} className="kz-btn kz-btn--primary">
              便を追加
            </button>
          ) : null
        }
      />
      {(create.isError || patch.isError) && <ErrorNote error={create.error ?? patch.error} />}
      {/* 便がゼロ＝発注が成立しない状態を明示する（放置すると「注文できない」の問い合わせになる） */}
      {!list.isLoading && activeCount === 0 && (
        <div data-testid="no-active-bin" style={warnBanner}>
          利用できる便がありません。取引先は注文時に配送便を選べないため、注文を送れません。
        </div>
      )}

      {list.isLoading ? (
        <Spinner />
      ) : list.isError ? (
        <ErrorNote error={list.error} />
      ) : rows.length === 0 ? (
        <Empty
          title="配送便がありません"
          hint={isAdmin ? "「便を追加」から登録してください（例：午前便 10:00）。" : "管理者に登録を依頼してください。"}
        />
      ) : (
        <Card padding="0">
          <Table head={["並び", "便", "締め時刻", "状態", "操作"]}>
            {rows.map((b) => (
              <tr key={b.id} data-testid={`bin-${b.id}`}>
                <Td>
                  <span className="kz-num">{b.sort_order}</span>
                </Td>
                <Td style={{ fontWeight: 600 }}>{b.name}</Td>
                <Td>
                  <span className="kz-num">{b.cutoff_time.slice(0, 5)}</span>
                </Td>
                <Td>
                  {b.status === "active" ? <Badge tone="primary">利用中</Badge> : <Badge>利用停止</Badge>}
                </Td>
                <Td>
                  <div style={{ display: "flex", gap: "var(--kz-space-2)" }}>
                    <button
                      type="button"
                      data-testid={`bin-edit-${b.id}`}
                      onClick={() => setEditing(b)}
                      className="kz-btn kz-btn--sm"
                    >
                      編集
                    </button>
                    <button
                      type="button"
                      data-testid={`bin-toggle-${b.id}`}
                      onClick={() => setToggling(b)}
                      className="kz-btn kz-btn--sm"
                    >
                      {b.status === "active" ? "利用停止にする" : "利用を再開する"}
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}

      {/* 配送料の設定（FR-131・D-90・M1.6）＝便の下に置く。
          便より先に出さないのは、便がゼロだと注文そのものが成立しない（＝先に直すべき）ため。 */}
      <DeliveryFeeSection isAdmin={isAdmin} />

      {creating && (
        <BinForm
          title="便を追加"
          busy={create.isPending}
          onSubmit={(v) => create.mutate(v)}
          onCancel={() => setCreating(false)}
        />
      )}
      {editing && (
        <BinForm
          title="便を編集"
          initial={editing}
          busy={patch.isPending}
          onSubmit={(v) =>
            patch.mutate({ id: editing.id, body: { ...v, updated_at: editing.updated_at } })
          }
          onCancel={() => setEditing(null)}
        />
      )}
      {toggling && (
        <ConfirmDialog
          title={toggling.status === "active" ? "この便を利用停止にしますか？" : "この便の利用を再開しますか？"}
          body={
            toggling.status === "active"
              ? "取引先の注文画面の選択肢から消えます。すでに入っている注文はそのまま残ります。"
              : "取引先の注文画面の選択肢に戻ります。"
          }
          confirmLabel={toggling.status === "active" ? "利用停止にする" : "利用を再開する"}
          busy={patch.isPending}
          onConfirm={() =>
            patch.mutate({
              id: toggling.id,
              body: {
                status: toggling.status === "active" ? "disabled" : "active",
                updated_at: toggling.updated_at,
              },
            })
          }
          onCancel={() => setToggling(null)}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* 配送料の設定（FR-131・D-90・M1.6）                                    */
/* ------------------------------------------------------------------ */

/** サーバの Decimal 文字列（"500" / "500.00"）を、入力欄に置ける**円の整数文字列**にする。 */
function yenString(v: string | null): string {
  return v === null || v === "" ? "" : String(Number(v));
}

/** 円として受け付ける形か（**整数のみ**）。小数を送るとサーバが 400 で弾く（04 §1 #5）ため、
 *  打っている途中に気づけるよう画面側でも同じ規則を持つ。 */
function isYen(v: string): boolean {
  return /^\d{1,12}$/.test(v);
}

function DeliveryFeeSection({ isAdmin }: { isAdmin: boolean }) {
  const q = useQuery({ queryKey: ["delivery-fee"], queryFn: () => console_.deliveryFee() });

  return (
    <section style={{ marginTop: "var(--kz-space-6)" }}>
      <h2 style={{ fontSize: "var(--kz-font-size-lg)", margin: "0 0 var(--kz-space-1)" }}>
        配送料の設定
      </h2>
      <p style={{ ...hintStyle, margin: "0 0 var(--kz-space-4)" }}>
        {/* 「相手の画面にどう出るか」を知らせる＝金額の設定を、見え方が分からないまま入れさせない */}
        取引先のアプリには、卸ごとにこの内容が表示されます。
        {!isAdmin && adminOnlyNote("変更")}
      </p>
      {q.isError ? (
        <ErrorNote error={q.error} />
      ) : q.data ? (
        <DeliveryFeeForm initial={q.data} isAdmin={isAdmin} />
      ) : (
        <Spinner />
      )}
    </section>
  );
}

/**
 * 配送料の入力（FR-131）。**条件は1段だけ**＝基本配送料＋無料になる注文金額（D-90③）。
 *
 * 便別・地域別・重量別を作らないのは、卸ごとの商習慣をまだ聞いていない（確定情報が無い）から。
 * 作れば必ず「実際の運用と合わない設定項目」が増え、卸は使わない欄を毎回読むことになる。
 * 足りない事情は補足の自由文で相手に伝える。
 */
function DeliveryFeeForm({ initial, isAdmin }: { initial: DeliveryFeeSetting; isAdmin: boolean }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"included" | "separate">(initial.mode);
  const [baseFee, setBaseFee] = useState(yenString(initial.base_fee));
  const [threshold, setThreshold] = useState(yenString(initial.free_threshold));
  const [note, setNote] = useState(initial.note ?? "");
  // 楽観ロック（規約10）の基準。**保存が通ったら応答の値へ進める**＝続けて2回保存できる
  // （フォームを作り直さないので、保存直後の「もう一度直す」で 409 にならない）。
  const [updatedAt, setUpdatedAt] = useState(initial.updated_at);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      console_.putDeliveryFee({
        mode,
        // ★「込み」に戻しても入力した額は捨てない＝別途に戻したときに打ち直させない。
        //   どちらの額を使うかは `mode` が決める（保存された額が勝手に効くことはない）。
        base_fee: baseFee === "" ? "0" : baseFee,
        free_threshold: threshold === "" ? null : threshold,
        note: note.trim() === "" ? null : note.trim(),
        updated_at: updatedAt,
      }),
    onSuccess: (row) => {
      setUpdatedAt(row.updated_at);
      setSaved(true);
      qc.setQueryData(["delivery-fee"], row);
    },
    onError: () => setSaved(false),
  });

  const separate = mode === "separate";
  // 別途にするなら金額が要る（空のまま保存すると「別途 0円」＝相手には無料に見える）
  const feeOk = !separate || isYen(baseFee);
  const thresholdOk = threshold === "" || isYen(threshold);
  const canSave = isAdmin && feeOk && thresholdOk && !save.isPending;

  const radio = (value: "included" | "separate", label: string, hint: string) => (
    <label
      style={{
        display: "flex",
        gap: "var(--kz-space-2)",
        alignItems: "flex-start",
        padding: "var(--kz-space-3)",
        border: `1px solid ${mode === value ? "var(--kz-color-primary)" : "var(--kz-color-border)"}`,
        borderRadius: "var(--kz-radius-sm)",
        background: mode === value ? "var(--kz-color-primary-weak)" : "var(--kz-color-surface)",
        cursor: isAdmin ? "pointer" : "default",
      }}
    >
      <input
        type="radio"
        name="delivery-fee-mode"
        data-testid={`delivery-fee-mode-${value}`}
        checked={mode === value}
        disabled={!isAdmin}
        onChange={() => {
          setMode(value);
          setSaved(false);
        }}
        style={{ width: 18, height: 18, marginTop: 2 }}
      />
      <span>
        <span style={{ fontWeight: 600 }}>{label}</span>
        <span style={{ ...hintStyle, display: "block" }}>{hint}</span>
      </span>
    </label>
  );

  /** 409（誰かが先に保存した）からの戻り道。**これが無いと詰む**＝手元の `updated_at` は
   *  古いままなので、同じボタンを何度押しても 409 が返り続ける（画面から抜け出せない）。 */
  const reload = async () => {
    const fresh = await qc.fetchQuery({
      queryKey: ["delivery-fee"],
      queryFn: () => console_.deliveryFee(),
      staleTime: 0,
    });
    setMode(fresh.mode);
    setBaseFee(yenString(fresh.base_fee));
    setThreshold(yenString(fresh.free_threshold));
    setNote(fresh.note ?? "");
    setUpdatedAt(fresh.updated_at);
    setSaved(false);
    save.reset();
  };
  const isConflict = save.error instanceof ApiError && save.error.code === "conflict_stale_update";

  return (
    <Card>
      {save.isError && <ErrorNote error={save.error} />}
      {isConflict && (
        <p style={{ margin: "0 0 var(--kz-space-3)" }}>
          <button type="button" data-testid="delivery-fee-reload" onClick={reload} className="kz-btn">
            最新の設定を読み込む
          </button>
        </p>
      )}
      <div style={{ display: "grid", gap: "var(--kz-space-3)", maxWidth: 620 }}>
        {radio(
          "included",
          "商品価格に含む",
          "配送料は別に頂きません。取引先のアプリには配送料の行を出しません。",
        )}
        {radio("separate", "別途いただく", "ご注文ごとに配送料を頂きます。条件は下で決めます。")}

        {separate && (
          <>
            <label style={labelStyle}>
              基本配送料
              <input
                type="text"
                inputMode="numeric"
                data-testid="delivery-fee-base"
                value={baseFee}
                disabled={!isAdmin}
                onChange={(e) => {
                  setBaseFee(e.target.value.trim());
                  setSaved(false);
                }}
                // ★**例の金額を置かない**（D-90／TBD-22＝設計書にも画面にも具体額を書かない）＝
                //   卸が自分の商売で決める値で、こちらが数字を示すと「相場」に見える。
                style={{ ...inputStyle, maxWidth: 220 }}
              />
              <span style={hintStyle}>
                1回のご注文あたりの金額です。円・整数で入れてください（税の扱いは今の設定に含みません）。
              </span>
              {!feeOk && (
                <span style={{ ...hintStyle, color: "var(--kz-status-danger)" }}>
                  金額を円・整数で入れてください。
                </span>
              )}
            </label>

            <label style={labelStyle}>
              この金額以上のご注文で配送料無料
              <input
                type="text"
                inputMode="numeric"
                data-testid="delivery-fee-threshold"
                value={threshold}
                disabled={!isAdmin}
                onChange={(e) => {
                  setThreshold(e.target.value.trim());
                  setSaved(false);
                }}
                style={{ ...inputStyle, maxWidth: 220 }}
              />
              {/* ★空欄と 0 は別物＝0 は「0円以上で無料」＝常に無料になる。取り違えると
                  「無料にしたつもりが無い」ではなく「有料にしたつもりが全部無料」になる。 */}
              <span style={hintStyle}>空欄にすると、無料になる条件はありません（0 と空欄は違います）。</span>
              {!thresholdOk && (
                <span style={{ ...hintStyle, color: "var(--kz-status-danger)" }}>
                  金額を円・整数で入れてください。空欄にすると条件なしになります。
                </span>
              )}
            </label>
          </>
        )}

        <label style={labelStyle}>
          補足（任意）
          <input
            type="text"
            data-testid="delivery-fee-note"
            value={note}
            disabled={!isAdmin}
            maxLength={200}
            onChange={(e) => {
              setNote(e.target.value);
              setSaved(false);
            }}
            placeholder="遠方は別途お見積りとなります"
            style={{ ...inputStyle, width: "100%" }}
          />
          <span style={hintStyle}>
            取引先のアプリに、そのまま表示されます（200字まで）。地域別・重量別などの条件は設定できないため、
            事情はこちらでお伝えください。
          </span>
        </label>

        {/* 相手の画面にどう出るか＝**金額の設定を、見え方が分からないまま入れさせない**（05 §6.1 C-18） */}
        <div
          data-testid="delivery-fee-preview"
          style={{
            padding: "var(--kz-space-3)",
            borderRadius: "var(--kz-radius-sm)",
            background: "var(--kz-color-surface-sunken)",
            border: "1px solid var(--kz-color-border)",
          }}
        >
          <div style={{ ...hintStyle, marginBottom: "var(--kz-space-1)" }}>取引先のアプリでの見え方</div>
          <div style={{ fontWeight: 600 }}>
            {/* 金額が入っていないうちに「配送料 0円」と出さない＝こちらが決めた額のように読める */}
            {separate && !feeOk
              ? "基本配送料を入れると、ここに見え方が出ます。"
              : deliveryFeePreview(mode, baseFee, thresholdOk && threshold !== "" ? threshold : null)}
          </div>
          {note.trim() !== "" && (
            <div style={{ ...hintStyle, marginTop: "var(--kz-space-1)" }}>{note.trim()}</div>
          )}
        </div>

        {isAdmin && (
          <div style={{ display: "flex", alignItems: "center", gap: "var(--kz-space-3)" }}>
            <button
              type="button"
              data-testid="delivery-fee-save"
              disabled={!canSave}
              onClick={() => save.mutate()}
              className="kz-btn kz-btn--primary"
            >
              {save.isPending ? "保存しています…" : "配送料の設定を保存"}
            </button>
            {saved && (
              <span data-testid="delivery-fee-saved" style={{ color: "var(--kz-status-ok)", fontWeight: 600 }}>
                保存しました。
              </span>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

/** 便の入力（新規・編集の共通）。締め時刻は **JST の壁時計**（04 §5＝time 型・TZ 変換しない）。 */
function BinForm({
  title,
  initial,
  busy,
  onSubmit,
  onCancel,
}: {
  title: string;
  initial?: Bin;
  busy: boolean;
  onSubmit: (v: { name: string; cutoff_time: string; sort_order: number }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  // U5：**新規は空**にする。既定値 "10:00" を最初から入れておくと、まだ何も決めていないのに
  // 「入力済み」に見える（森井氏の指摘＝「配送便の色が濃くて入力されているのかと思った」）。
  const [cutoff, setCutoff] = useState(initial ? initial.cutoff_time.slice(0, 5) : "");
  const [sortOrder, setSortOrder] = useState(String(initial?.sort_order ?? 0));

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-label={title}>
      <Card>
        <h2 style={{ margin: 0, fontSize: "var(--kz-font-size-lg)" }}>{title}</h2>
        <div style={{ display: "grid", gap: "var(--kz-space-3)", marginTop: "var(--kz-space-4)" }}>
          <label style={labelStyle}>
            便の名前
            <input
              data-testid="bin-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="午前便"
              maxLength={60}
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            締め時刻
            <input
              type="time"
              data-testid="bin-cutoff"
              value={cutoff}
              onChange={(e) => setCutoff(e.target.value)}
              required
              style={inputStyle}
            />
            <span style={hintStyle}>この時刻を過ぎた注文は、その便では受けられません。</span>
          </label>
          <label style={labelStyle}>
            並び順
            <input
              type="number"
              data-testid="bin-sort"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              style={inputStyle}
            />
            <span style={hintStyle}>小さいほど上に出ます。</span>
          </label>
        </div>
        <div style={{ display: "flex", gap: "var(--kz-space-2)", marginTop: "var(--kz-space-5)", justifyContent: "flex-end" }}>
          <button type="button" onClick={onCancel} className="kz-btn">
            やめる
          </button>
          <button
            type="button"
            data-testid="bin-save"
            disabled={busy || name.trim() === ""}
            onClick={() =>
              onSubmit({ name: name.trim(), cutoff_time: `${cutoff}:00`, sort_order: Number(sortOrder) || 0 })
            }
            className="kz-btn kz-btn--primary"
          >
            保存する
          </button>
        </div>
      </Card>
    </div>
  );
}
